import React from "react";
import { Skill } from "../sample.data";
import { getVisibleSortedItems } from "../utils";

interface SkillsGridProps {
  skills: Skill[];
}

const SkillCard: React.FC<{ skill: Skill }> = ({ skill }) => {
  const getLevelConfig = (level?: string) => {
    switch (level) {
      case "expert":
        return {
          color: "from-emerald-500 to-green-600",
          bg: "bg-emerald-50 dark:bg-emerald-900/20",
          text: "text-emerald-700 dark:text-emerald-300",
          border: "border-emerald-200 dark:border-emerald-800",
          percentage: 90,
        };
      case "advanced":
        return {
          color: "from-blue-500 to-indigo-600",
          bg: "bg-blue-50 dark:bg-blue-900/20",
          text: "text-blue-700 dark:text-blue-300",
          border: "border-blue-200 dark:border-blue-800",
          percentage: 75,
        };
      case "intermediate":
        return {
          color: "from-amber-500 to-orange-600",
          bg: "bg-amber-50 dark:bg-amber-900/20",
          text: "text-amber-700 dark:text-amber-300",
          border: "border-amber-200 dark:border-amber-800",
          percentage: 60,
        };
      case "beginner":
        return {
          color: "from-gray-500 to-slate-600",
          bg: "bg-gray-50 dark:bg-gray-900/20",
          text: "text-gray-700 dark:text-gray-300",
          border: "border-gray-200 dark:border-gray-800",
          percentage: 30,
        };
      default:
        return {
          color: "from-gray-400 to-gray-500",
          bg: "bg-gray-50 dark:bg-gray-900/20",
          text: "text-gray-600 dark:text-gray-400",
          border: "border-gray-200 dark:border-gray-700",
          percentage: 50,
        };
    }
  };

  const config = getLevelConfig(skill.level);

  return (
    <div className="group bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-300 overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-900 dark:text-white">
            {skill.name}
          </h4>
          {skill.level && (
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text} ${config.border} border`}
            >
              {skill.level}
            </span>
          )}
        </div>

        {/* Progress indicator */}
        {skill.level && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>Proficiency</span>
              <span>{config.percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${config.color} transition-all duration-500 ease-out`}
                style={{ width: `${config.percentage}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          {skill.yearsOfExperience && (
            <span>{skill.yearsOfExperience} years experience</span>
          )}
          {skill.endorsements && skill.endorsements > 0 && (
            <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full">
              {skill.endorsements} endorsements
            </span>
          )}
        </div>

        {skill.certifications && skill.certifications.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {skill.certifications.map((cert) => (
              <span
                key={cert}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs rounded-md border border-gray-200 dark:border-gray-700"
              >
                {cert}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Subtle accent line */}
      <div className={`h-1 bg-gradient-to-r ${config.color} opacity-60`}></div>
    </div>
  );
};

export const SkillsGrid: React.FC<SkillsGridProps> = ({ skills }) => {
  const visibleSkills = getVisibleSortedItems(skills);

  if (visibleSkills.length === 0) return null;

  // Group skills by category
  const groupedSkills = visibleSkills.reduce(
    (groups, skill) => {
      const category = skill.category || "other";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(skill);
      return groups;
    },
    {} as Record<string, Skill[]>
  );

  const getCategoryConfig = (category: string) => {
    const configs: Record<string, { icon: string; color: string }> = {
      technical: { icon: "💻", color: "text-blue-600 dark:text-blue-400" },
      soft: { icon: "🤝", color: "text-purple-600 dark:text-purple-400" },
      language: { icon: "🌍", color: "text-green-600 dark:text-green-400" },
      design: { icon: "🎨", color: "text-pink-600 dark:text-pink-400" },
      other: { icon: "⚡", color: "text-gray-600 dark:text-gray-400" },
    };
    return configs[category] || configs.other;
  };

  // If we have multiple categories, render them grouped
  if (Object.keys(groupedSkills).length > 1) {
    return (
      <div className="space-y-8">
        {Object.entries(groupedSkills).map(([category, categorySkills]) => {
          const categoryConfig = getCategoryConfig(category);
          return (
            <div key={category}>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-2xl">{categoryConfig.icon}</span>
                <div>
                  <h3
                    className={`text-lg font-semibold capitalize ${categoryConfig.color}`}
                  >
                    {category} Skills
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {categorySkills.length} skills
                  </p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {categorySkills.map((skill) => (
                  <SkillCard key={skill.id} skill={skill} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Simple grid view for uncategorized skills
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {visibleSkills.map((skill) => (
        <SkillCard key={skill.id} skill={skill} />
      ))}
    </div>
  );
};
